import type { SetlistContext } from './client';
import { STOCK_VIDEO_COLLECTION } from './collections';
import type { VideoDocument } from './stock-video-types';

export const fetchSimilarStockVideos = async (
  context: SetlistContext,
  {
    styles = [],
    themes = [],
    types = [],
    keywords = [],
    title,
    page,
    additionalFilter = '',
    resultsPerPage = 12,
  }: {
    keywords?: string[];
    styles?: string[];
    themes?: string[];
    types?: string[];
    title: string;
    page: number;
    additionalFilter?: string;
    resultsPerPage?: number;
  }
) => {
  // Group filters by category using OR logic within each group
  const styleFilter =
    styles.length > 0
      ? `(${styles.map((style) => `styles.id:=[${String(style)}]`).join(' || ')})`
      : '';

  const themeFilter =
    themes.length > 0
      ? `(${themes.map((theme) => `themes.id:=[${String(theme)}]`).join(' || ')})`
      : '';

  const typeFilter =
    types.length > 0
      ? `(${types.map((type) => `types.id:=[${String(type)}]`).join(' || ')})`
      : '';

  // Combine category filters with OR logic
  const categoryFilters = [styleFilter, themeFilter, typeFilter].filter(
    Boolean
  );

  // Base filter string (always includes compatibility and name exclusion)
  let filterString =
    categoryFilters.length > 0 && keywords.length === 0
      ? `(${categoryFilters.join(' || ')}) && title:!\`${title}\``
      : `title:!\`${title}\``;

  // Add additional filter if provided
  if (additionalFilter) {
    filterString = `${filterString} && ${additionalFilter}`;
  }

  const searchParams = {
    q: keywords.length > 0 ? [title, ...keywords].join(', ') : '*',
    query_by: 'keywords, title',
    filter_by: filterString,
    page,
    per_page: resultsPerPage,
    prioritize_exact_match: true,
    sort_by: keywords.length > 0 ? '_text_match:desc' : undefined,
    num_typos: 0,
    min_len_1typo: 999, // Effectively disable typo tolerance
    min_len_2typo: 999, // Effectively disable typo tolerance
    exhaustive_search: true,
    drop_tokens_threshold: 0,
    prioritize_token_position: true,
    prioritize_num_matching_fields: true,
  };

  const response = await context.client
    .collections(STOCK_VIDEO_COLLECTION)
    .documents()
    .search(searchParams, {});

  const { page: currentPage, found, hits } = response;

  let hasMore = false;

  if (hits) {
    hasMore = resultsPerPage * (currentPage - 1) + hits.length < found;
  }

  if (!hits || hits.length === 0) {
    return {
      data: [],
      hasMore: false,
    };
  }

  return {
    data: response?.hits?.map((hit) => hit.document as VideoDocument) ?? [],
    hasMore,
  };
};
